import { FunctionComponent, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../configuration/setup/hooks';
import { useGetOutgoingVehicleIdsMutation } from '../../../api/compoundManagementApi';
import { handleQueryError } from '../../../notifications/ErrorNotification';
import {
    compoundManagementSlice,
    getCompoundManagementModalFilter,
    getVinSearchFilter,
    mapOutgoingVehiclesFilterQueryParams,
} from '../../../reducers/compoundManagementSlice';
import { BulkSelectionDropdown } from '../../../sharedComponents/table/BulkSelectionDropdown';
import { FilterMaterializationExceededErrorResponseCodec } from '../../../api/types/compoundManagement/errorCodes';

export const FIXED_OUTGOING_CACHE_KEY = 'shared-get-outgoing-vehicle-ids';
export const MAX_SELECTION_SIZE = 10_000;

export const OutgoingVehicleBulkSelectionDropdown: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const { getIdsToUpdateSelection, hasTooManyFilterResultsError, resetTooManyFilterResultsError } =
        useGetOutgoingVehicleIds();

    const handleAddFilterResultToSelection = async () => {
        const ids = await getIdsToUpdateSelection();
        if (ids !== undefined) {
            dispatch(compoundManagementSlice.actions.addToOutgoingBulkSelection(ids));
        }
    };

    const handleRemoveFilterResultFromSelection = async () => {
        const ids = await getIdsToUpdateSelection();
        if (ids !== undefined) {
            dispatch(compoundManagementSlice.actions.removeFromOutgoingBulkSelection(ids));
        }
    };

    const handleClearSelection = () => {
        dispatch(compoundManagementSlice.actions.clearOutgoingBulkSelection());
    };

    return (
        <BulkSelectionDropdown
            addFilterResultToSelection={handleAddFilterResultToSelection}
            removeFilterResultFromSelection={handleRemoveFilterResultFromSelection}
            clearSelection={handleClearSelection}
            hasTooManyFilterResultsError={hasTooManyFilterResultsError}
            maxSelectionSize={MAX_SELECTION_SIZE}
            resetTooManyFilterResultsError={resetTooManyFilterResultsError}
        />
    );
};

const useGetOutgoingVehicleIds = () => {
    const filters = useAppSelector(getCompoundManagementModalFilter);
    const vinSearch = useAppSelector(getVinSearchFilter);

    const [getCompoundVehicleIds] = useGetOutgoingVehicleIdsMutation({ fixedCacheKey: FIXED_OUTGOING_CACHE_KEY });
    const [hasTooManyFilterResultsError, setHasTooManyFilterResultsError] = useState(false);

    const getIdsToUpdateSelection = async (): Promise<string[] | undefined> => {
        try {
            const filterParams = mapOutgoingVehiclesFilterQueryParams(filters, vinSearch);
            return await getCompoundVehicleIds(filterParams).unwrap();
        } catch (error) {
            if (FilterMaterializationExceededErrorResponseCodec.is(error)) {
                setHasTooManyFilterResultsError(true);
            } else {
                handleQueryError(error);
            }
        }
    };

    const resetTooManyFilterResultsError = () => setHasTooManyFilterResultsError(false);

    return { getIdsToUpdateSelection, hasTooManyFilterResultsError, resetTooManyFilterResultsError };
};
